import _ from 'lodash'
import { withDependencies, named, optional } from '@wix/thunderbolt-ioc'
import {
	PageFeatureConfigSymbol,
	MasterPageFeatureConfigSymbol,
	EditorFeatureConfigSymbol,
	BrowserWindowSymbol,
	Props,
	BrowserWindow,
	IPropsStore,
	IPageDidMountHandler,
	IPageWillUnmountHandler,
	pageIdSym,
	FeatureStateSymbol,
} from '@wix/thunderbolt-symbols'
import type { IFeatureState } from 'thunderbolt-feature-state'
import type { IModelUpdatesHandlersImplementor } from 'ds-feature-model-updates-invoker'
import { INavigationManager, NavigationManagerSymbol } from 'feature-navigation-manager'
import type {
	PageAnchorsHandlers,
	PageAnchorsModelUpdateConfig,
	PageAnchorsPageConfig,
	PageAnchorsEditorConfig,
	PageAnchorsMasterPageConfig,
} from '../types'
import { name } from '../symbols'
import { createAnchorObserver } from '../pageAnchorsUtils'

const observePageAnchors = (
	pageConfig: PageAnchorsPageConfig,
	masterPageConfig: PageAnchorsMasterPageConfig,
	pageId: string,
	siteOffset: number,
	window: BrowserWindow,
	propsStore: IPropsStore
): (() => void) => {
	// For menus which are rendered in the add panel as a mini site
	if (!pageId) {
		return _.noop
	}
	const pageAnchorsObservers = [
		...(pageConfig?.pageAnchorsObservers || []),
		...(masterPageConfig?.pageAnchorsObservers || []),
	]
	const activeAnchorObservers = [
		...(pageConfig?.activeAnchorObservers || []),
		...(masterPageConfig?.activeAnchorObservers || []),
	]

	const anchors = [...(pageConfig?.anchors || []), ...(masterPageConfig?.anchors || [])]
	const hasObservers = pageAnchorsObservers.length || activeAnchorObservers.length

	if (hasObservers) {
		const observeAnchors = createAnchorObserver(
			pageAnchorsObservers,
			activeAnchorObservers,
			anchors,
			window,
			propsStore,
			pageId,
			siteOffset,
			true
		)

		return observeAnchors()
	}
	return _.noop
}

const editorPageAnchorsFactory = (
	pageFeatureConfig: PageAnchorsPageConfig,
	editorMasterPageConfig: PageAnchorsMasterPageConfig,
	editorPageConfig: PageAnchorsEditorConfig,
	featureState: IFeatureState<{ observersCleanup: () => void }>,
	window: BrowserWindow,
	propsStore: IPropsStore,
	navigationManager: INavigationManager,
	pageIdFromPageContainer: string
): IModelUpdatesHandlersImplementor<PageAnchorsHandlers> & IPageDidMountHandler & IPageWillUnmountHandler => {
	// TODO
	// 1. do not bind the same factory to 2 containers (editor, editorPage), since you probably don't want the factory to be instantiated 3 times, also not all symbols are available on the editor container (pageId, contextId).
	// 2. code it so that `onAnchorUpdate` is invoked only upon changes, not on load.
	// 3. cleanup is not necessary in `pageWillUnmount` if we cleanup every time before we observe.
	// 4. remove the debounce around the scroll handler, scroll events are debounced natively. another option is to throttle instead of debouncing.
	// 5. we probably don't want to run the page lifecycle apis upon lightbox opening.
	// 6. (comp/feature) BAD BUG going to preview => back to editor => preview again => click on anchor link => explodes, invalid relative href attribute is set on the link.
	// 7. (component) hide the anchor name not only on blur, but also when the active anchor is changed

	return {
		featureName: name,
		handlers: {
			onAnchorUpdate(pageUpdateConfig: PageAnchorsModelUpdateConfig) {
				if (navigationManager.isDuringNavigation() || navigationManager.isFirstNavigation()) {
					return
				}

				const { pageConfig, masterPageConfig, editorConfig } = pageUpdateConfig
				const { pageId, siteOffset } = editorConfig

				featureState.get()?.observersCleanup?.()
				const observersCleanup = observePageAnchors(
					pageConfig,
					masterPageConfig,
					pageId,
					siteOffset,
					window,
					propsStore
				)
				featureState.update(() => ({ observersCleanup }))
			},
		},
		pageDidMount(): void {
			if (!editorPageConfig.pageId || pageIdFromPageContainer === 'masterPage') {
				return
			}

			featureState.get()?.observersCleanup?.()
			const observersCleanup = observePageAnchors(
				pageFeatureConfig,
				editorMasterPageConfig,
				pageIdFromPageContainer,
				editorPageConfig.siteOffset,
				window,
				propsStore
			)
			featureState.update(() => ({ observersCleanup }))
		},
		pageWillUnmount(): void {
			featureState.get()?.observersCleanup?.()
		},
	}
}

export const EditorPageAnchors = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		named(MasterPageFeatureConfigSymbol, name),
		named(EditorFeatureConfigSymbol, name),
		named(FeatureStateSymbol, name),
		BrowserWindowSymbol,
		Props,
		NavigationManagerSymbol,
		optional(pageIdSym),
	],
	editorPageAnchorsFactory
)
